import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import SlideNavigation from "./SlideNavigation";
import { slideData } from "@/data/slideData";
import TitleSlide from "./slides/TitleSlide";
import ProblemSlide from "./slides/ProblemSlide";
import SolutionSlide from "./slides/SolutionSlide";
import ProductSlide from "./slides/ProductSlide";
import WhyNowSlide from "./slides/WhyNowSlide";
import MarketSlide from "./slides/MarketSlide";
import BusinessModelSlide from "./slides/BusinessModelSlide";
import TractionSlide from "./slides/TractionSlide";
import GoToMarketSlide from "./slides/GoToMarketSlide";
import CompetitionSlide from "./slides/CompetitionSlide";
import TeamSlide from "./slides/TeamSlide";
import FinancialsSlide from "./slides/FinancialsSlide";
import FundraisingSlide from "./slides/FundraisingSlide";
import VisionSlide from "./slides/VisionSlide";

const slideComponents = [
  TitleSlide,
  ProblemSlide,
  SolutionSlide,
  ProductSlide,
  WhyNowSlide,
  MarketSlide,
  BusinessModelSlide,
  TractionSlide,
  GoToMarketSlide,
  CompetitionSlide,
  TeamSlide,
  FinancialsSlide,
  FundraisingSlide,
  VisionSlide,
];

export default function PitchDeck() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showNotes, setShowNotes] = useState(false);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => Math.min(prev + 1, 13));
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => Math.max(prev - 1, 0));
  }, []);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        nextSlide();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        prevSlide();
      } else if (e.key === "s" || e.key === "S") {
        e.preventDefault();
        setShowNotes((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nextSlide, prevSlide]);

  const handleExport = () => {
    window.print();
  };

  const CurrentSlide = slideComponents[currentSlide];

  return (
    <>
      <div
        className="screen-only h-screen w-full flex flex-col bg-slate-100 overflow-hidden"
        data-testid="presentation-container"
      >
        <div className="flex-1 flex items-center justify-center p-2 sm:p-6 min-h-0 overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="slide-canvas"
              onClick={nextSlide}
              data-testid={`slide-${currentSlide + 1}`}
            >
              <div className="absolute top-2.5 right-4 z-50">
                <span className="text-[8px] sm:text-[9px] font-bold tracking-[0.2em] uppercase text-slate-300 font-body">
                  CONFIDENTIAL
                </span>
              </div>
              <CurrentSlide />
            </motion.div>
          </AnimatePresence>
        </div>

        <SlideNavigation
          currentSlide={currentSlide}
          totalSlides={14}
          onPrev={prevSlide}
          onNext={nextSlide}
          onGoToSlide={goToSlide}
          onToggleNotes={() => setShowNotes((prev) => !prev)}
          onExport={handleExport}
          showNotes={showNotes}
        />

        <Sheet open={showNotes} onOpenChange={setShowNotes}>
          <SheetContent
            side="right"
            className="w-[380px] sm:w-[480px] sm:max-w-[480px]"
          >
            <SheetHeader>
              <SheetTitle className="font-heading text-lg tracking-tight">
                Speaker Notes
              </SheetTitle>
              <SheetDescription className="text-xs text-slate-400 font-body">
                Slide {currentSlide + 1} of 14 &mdash;{" "}
                {slideData[currentSlide].title}
              </SheetDescription>
            </SheetHeader>
            <div className="mt-6">
              <p
                className="text-slate-600 leading-relaxed text-base font-body"
                data-testid="speaker-notes-content"
              >
                {slideData[currentSlide].speakerNote}
              </p>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <div className="print-only">
        {slideComponents.map((SlideComponent, index) => (
          <div key={index} className="print-slide relative">
            <div className="absolute top-2.5 right-4 z-50">
              <span className="text-[9px] font-bold tracking-[0.2em] uppercase text-slate-300 font-body">
                CONFIDENTIAL
              </span>
            </div>
            <SlideComponent />
          </div>
        ))}
      </div>
    </>
  );
}
